/**
 * POS order model — the single source of truth for money formatting, line
 * labels, signed amounts, and grouping. Every POS surface (the sell screen's
 * cart + tender popups + receipt + review modal, and the customer display)
 * imports these instead of hand-rolling its own, so the scheme stays uniform.
 */
import type { PosOrderLine } from "../../api/client";

/** Cents → euro string: locale-grouped, 2 decimals, leading minus, € prefix. */
export const eur = (cents: number): string =>
  `${cents < 0 ? "-" : ""}€${(Math.abs(cents) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** A settlement timestamp → HH:MM (local). */
export const hhmm = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "--:--"
    : `${String(d.getHours()).padStart(2, "0")}:${String(
        d.getMinutes(),
      ).padStart(2, "0")}`;
};

/** Signed cart amount for a line — matches the backend ``recompute_total``:
 * sale + deposit ADD (deposit qty is signed +charge/−refund), return SUBTRACTS,
 * comp is €0. So a Cup-in / Bottle-in shows as a negative (a refund). */
export const lineSignedCents = (l: PosOrderLine): number => {
  switch (l.kind) {
    case "sale":
    case "deposit":
      return l.unit_price_cents * l.qty;
    case "return":
      return -(l.unit_price_cents * l.qty);
    case "comp":
      return 0;
  }
};

/** Human label for a cart line. The cup is the configured cup article sold as a
 * sale (out) / returned (in); deposits carry their direction in the qty sign.
 * A normal sale/return resolves the article name from ``nameById`` (the loaded
 * catalog), falling back to ``Art #<id>`` only when the id is unknown. */
export const lineLabel = (
  l: PosOrderLine,
  cupArticleId: number | null,
  nameById: Map<number, string>,
): string => {
  // An ad-hoc custom drink carries its own name (no article to resolve).
  if (l.custom_name) return l.custom_name;
  const isCup = cupArticleId != null && l.article_id === cupArticleId;
  if (l.kind === "deposit") return l.qty < 0 ? "Bottle in" : "Bottle out";
  if (isCup) return l.kind === "return" ? "Cup in" : "Cup out";
  if (l.kind === "return") return "Return";
  if (l.kind === "comp") return "Comp";
  const name = l.article_id != null ? nameById.get(l.article_id) : undefined;
  return name ?? `Art #${l.article_id}`;
};

export type OrderGroup = { label: string; qty: number; cents: number };

/** Group lines by label → qty × name + signed total. `predicate` optionally
 * restricts which lines are counted (e.g. sale-only for the comp confirm). */
export const groupOrderLines = (
  lines: PosOrderLine[],
  cupId: number | null,
  nameById: Map<number, string>,
  predicate: (l: PosOrderLine) => boolean = () => true,
): OrderGroup[] => {
  const m = new Map<string, OrderGroup>();
  for (const l of lines) {
    if (!predicate(l)) continue;
    const label = lineLabel(l, cupId, nameById);
    const g = m.get(label) ?? { label, qty: 0, cents: 0 };
    g.qty += l.qty;
    g.cents += lineSignedCents(l);
    m.set(label, g);
  }
  return [...m.values()];
};
